import React, { useMemo, useCallback } from 'react'
import { UserService } from '@duck/api-definitions'
import { Checkbox } from '@duck/ui'
import { useTar } from '@/modules/i18n'
import { toast } from 'sonner'
import { RelativeDateTime } from '../../datetime/RelativeDateTime'
import { CohortPersonDetails } from '../cohortPageData'
import { useTableSettings } from '@/hooks/TableSettings/useTableSettings'
import { NoDataEmptyState } from '@/components/EmptyStates'
import { FunnelIcon } from '@heroicons/react/24/outline'
import {
  getDefaultVisibleColumns,
  createToggleColumnVisibilityWithProtectedColumn,
  renderSortIcon,
} from '@/hooks/TableSettings/generalTable.utils'
import {
  allColumns,
  type CohortColumnDef,
  type ColumnKey,
  getOnboardingStatusBadgeColor,
  formatOnboardingStatus,
  getAssignmentStatusBadgeColor,
  formatAssignmentStatus,
  getOnboardingStatusOptions,
  getAssignmentStatusOptions,
  sortCohortPeopleDetails,
  filterCohortPeopleDetails,
} from '@/hooks/TableSettings/cohortPeopleTable.utils'
import {
  TextFilterPopover,
  EnumFilterDropdown,
  ColumnVisibilityDropdown,
  ClearAllSettingsButton,
} from '@/hooks/TableSettings/tableSettings.components'

interface CohortPeopleTableProps {
  cohortId: string
  peopleDetails: CohortPersonDetails[]
  persons: UserService.Person[]
  checkedPersons: Set<string>
  setCheckedPersons: React.Dispatch<React.SetStateAction<Set<string>>>
  view?: 'all' | 'task'
}

export const CohortPeopleTable: React.FC<CohortPeopleTableProps> = ({
  cohortId,
  peopleDetails,
  persons,
  checkedPersons,
  setCheckedPersons,
  view = 'all',
}) => {
  const { t } = useTar()

  // Get columns available for current view
  const availableColumns = useMemo(() =>
    allColumns.filter(col => col.isAvailableInView(view)),
    [view]
  )

  // Initialize default visible columns based on view
  const defaultVisibleColumns = useMemo(() =>
    getDefaultVisibleColumns(availableColumns),
    [availableColumns]
  )

  const {
    sortColumn,
    sortDirection,
    toggleSort,
    isColumnVisible,
    toggleColumnVisibility: originalToggleColumnVisibility,
    getSortState,
    getColumnFilter,
    setColumnFilter,
    getColumnFilterArray,
    toggleColumnFilterArrayItem,
    clearColumnFilter,
    clearAllSettings,
    hasActiveSettings,
  } = useTableSettings({
    tableId: `cohortPeople-${cohortId}`,
    defaultVisibleColumns,
  })

  // Prevent toggling off the name column
  const toggleColumnVisibility = useMemo(
    () => createToggleColumnVisibilityWithProtectedColumn(originalToggleColumnVisibility, 'personId'),
    [originalToggleColumnVisibility]
  )

  // Generate enum options for filter dropdowns
  const onboardingStatusOptions = useMemo(() => getOnboardingStatusOptions(t), [t])
  const assignmentStatusOptions = useMemo(() => getAssignmentStatusOptions(t), [t])

  const getEnumOptionsForColumnMemo = useCallback((columnKey: ColumnKey) => {
    if (columnKey === 'onboardingStatus') return onboardingStatusOptions
    if (columnKey === 'assignmentStatus') return assignmentStatusOptions
    return undefined
  }, [onboardingStatusOptions, assignmentStatusOptions])

  // Filter columns to show only visible ones
  // Always include personId (name column) even if it's toggled off
  const visibleColumns = useMemo(() => {
    const filtered = availableColumns.filter(col => {
      return col.key === 'personId' || isColumnVisible(col.key)
    })
    return filtered
  }, [availableColumns, isColumnVisible])

  // Sort the data based on sort state
  const sortedPeopleDetails = useMemo(() =>
    sortCohortPeopleDetails(peopleDetails, persons, sortColumn, sortDirection),
    [peopleDetails, persons, sortColumn, sortDirection]
  )

  // Apply filters to sorted data
  const filteredAndSortedPeopleDetails = useMemo(() =>
    filterCohortPeopleDetails(
      sortedPeopleDetails,
      persons,
      availableColumns,
      getColumnFilter,
      getColumnFilterArray
    ),
    [sortedPeopleDetails, persons, availableColumns, getColumnFilter, getColumnFilterArray]
  )

  // Check if we have people but filters result in empty list
  const hasPeopleButNoResults = peopleDetails.length > 0 && filteredAndSortedPeopleDetails.length === 0
  const hasActiveFilters = hasActiveSettings()

  return (
    <div className="overflow-x-auto rounded-lg">
      <table className="min-w-full divide-y divide-border">
        <thead className="bg-secondary/5">
          <tr>
            {visibleColumns.map((col) => {
              const isEnumFilter = col.filterType === 'enum'
              const filterValue = isEnumFilter ? undefined : getColumnFilter(col.key)
              const filterArray = isEnumFilter ? getColumnFilterArray(col.key) : undefined
              const hasFilter = isEnumFilter ? (filterArray?.length || 0) > 0 : !!filterValue?.trim()
              const enumOptions = isEnumFilter ? getEnumOptionsForColumnMemo(col.key) : undefined

              return (
                <th
                  key={col.key}
                  className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                >
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleSort(col.key)}
                      className="group flex items-center gap-1.5 hover:text-foreground transition-colors"
                    >
                      <span>{t(col.label)}</span>
                      {renderSortIcon(getSortState(col.key))}
                    </button>
                    {isEnumFilter && enumOptions ? (
                      <EnumFilterDropdown
                        label={col.label}
                        labelT={t}
                        enumOptions={enumOptions}
                        filterArray={filterArray || []}
                        hasFilter={hasFilter}
                        onToggleFilterItem={(value) => toggleColumnFilterArrayItem(col.key, value)}
                        onClearFilter={() => clearColumnFilter(col.key)}
                      />
                    ) : (
                      <TextFilterPopover
                        label={col.label}
                        labelT={t}
                        filterValue={filterValue || ''}
                        hasFilter={hasFilter}
                        onFilterChange={(value) => setColumnFilter(col.key, value)}
                        onClearFilter={() => clearColumnFilter(col.key)}
                      />
                    )}
                  </div>
                </th>
              )
            })}
            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground tracking-wider">
              <div className="flex items-center gap-2 justify-end">
                <ColumnVisibilityDropdown
                  availableColumns={availableColumns}
                  isColumnVisible={isColumnVisible}
                  toggleColumnVisibility={toggleColumnVisibility}
                  protectedColumnKeys={['personId']}
                />
                {hasActiveSettings() && (
                  <ClearAllSettingsButton onClear={clearAllSettings} />
                )}
              </div>
            </th>
          </tr>
        </thead>
        <tbody className="bg-card divide-y divide-border">
          {hasPeopleButNoResults ? (
            <tr>
              <td colSpan={visibleColumns.length + 1} className="px-6 py-8">
                <NoDataEmptyState
                  icon={FunnelIcon}
                  title={t('common.noFilterResults')}
                  description={t('common.noFilterResultsDescription')}
                  buttonText={hasActiveFilters ? t('common.resetFilters') : undefined}
                  onButtonClick={hasActiveFilters ? clearAllSettings : undefined}
                />
              </td>
            </tr>
          ) : (
            filteredAndSortedPeopleDetails.map((personDetails) => {
              const person = persons.find(person => person.id === personDetails.personId)
              const isChecked = checkedPersons.has(personDetails.personId)

              const renderCell = (col: CohortColumnDef): React.ReactNode => {
                const cellRenderers: Partial<Record<ColumnKey, () => React.ReactNode>> = {
                  personId: () => (
                    <td key="personId" className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className={`h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center ${isChecked ? 'hidden' : 'group-hover:hidden'}`}>
                            <span className="text-sm font-medium text-accent">
                              {person?.firstName?.charAt(0) || ''}
                              {person?.lastName?.charAt(0) || ''}
                            </span>
                          </div>
                          <div className={`h-10 w-10 ${isChecked ? 'flex' : 'hidden group-hover:flex'} items-center justify-center`}>
                            <Checkbox
                              className="h-5 w-5"
                              checked={isChecked}
                              onCheckedChange={(checked) => {
                                setCheckedPersons(prev => {
                                  const next = new Set(prev)
                                  if (checked) {
                                    next.add(personDetails.personId)
                                  } else {
                                    next.delete(personDetails.personId)
                                  }
                                  return next
                                })
                              }}
                            />
                          </div>
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-foreground">
                            {person?.firstName || ''} {person?.lastName || ''}
                          </p>
                        </div>
                      </div>
                    </td>
                  ),
                  email: () => (
                    <td key="email" className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm text-muted-foreground">{person?.email}</p>
                    </td>
                  ),
                  onboardingStatus: () => {
                    const handleCopyLink = async () => {
                      if (person?.invitationToken) {
                        const invitationLink = `${window.location.origin}/?token=${person.invitationToken}`
                        try {
                          await navigator.clipboard.writeText(invitationLink)
                          toast.success(t('common.linkCopied'))
                        } catch (error) {
                          console.error('Failed to copy link:', error)
                          toast.error(t('common.failedToCopyLink') + '\n' + invitationLink, {
                            duration: 15000,
                          })
                        }
                      }
                    }

                    return (
                      <td key="onboardingStatus" className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getOnboardingStatusBadgeColor(
                              personDetails.onboardingStatus
                            )}`}
                          >
                            {t(formatOnboardingStatus(personDetails.onboardingStatus, !!person?.invitationToken))}
                          </span>
                          {person?.invitationToken && personDetails.onboardingStatus === UserService.PersonOnboardingStatusEnum.Invited && (
                            <button
                              onClick={handleCopyLink}
                              className="text-primary underline hover:text-primary/80 text-xs cursor-pointer"
                            >
                              {t('common.copyLink')}
                            </button>
                          )}
                        </div>
                      </td>
                    )
                  },
                  createdAt: () => (
                    <td key="createdAt" className="px-6 py-4 whitespace-nowrap">
                      <RelativeDateTime
                        isoDateString={person?.createdAt ?? null}
                        className="text-sm text-muted-foreground"
                      />
                    </td>
                  ),
                  assignmentStatus: () => (
                    <td key="assignmentStatus" className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getAssignmentStatusBadgeColor(
                          personDetails.assignmentStatus
                        )}`}
                      >
                        {t(formatAssignmentStatus(!!person?.invitationToken, personDetails.assignmentStatus))}
                      </span>
                    </td>
                  ),
                  grade: () => (
                    <td key="grade" className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-foreground">
                        {personDetails.grade !== undefined ? personDetails.grade : t('common.BLANK')}
                      </span>
                    </td>
                  ),
                  feedback: () => (
                    <td key="feedback" className="px-6 py-4">
                      <span className="text-sm text-muted-foreground">
                        {personDetails.feedback || t('common.BLANK')}
                      </span>
                    </td>
                  ),
                  startDate: () => (
                    <td key="startDate" className="px-6 py-4 whitespace-nowrap">
                      <RelativeDateTime
                        isoDateString={personDetails.startDate ?? null}
                        className="text-sm text-muted-foreground"
                      />
                    </td>
                  ),
                  submitDate: () => (
                    <td key="submitDate" className="px-6 py-4 whitespace-nowrap">
                      <RelativeDateTime
                        isoDateString={personDetails.submitDate ?? null}
                        className="text-sm text-muted-foreground"
                      />
                    </td>
                  ),
                }

                const renderer = cellRenderers[col.key as ColumnKey]
                return renderer ? renderer() : null
              }

              return (
                <tr key={personDetails.personId} className="hover:bg-secondary/5 group">
                  {visibleColumns.map((col, index) => {
                    const cell = renderCell(col)
                    if (cell && index === visibleColumns.length - 1 && React.isValidElement(cell)) {
                      // Last cell should span 2 columns to match the header
                      return React.cloneElement(cell as React.ReactElement<{ colSpan?: number }>, { colSpan: 2 })
                    }
                    return cell
                  })}
                </tr>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}
